import React from 'react'
import { LoginStepAskPassword } from './LoginStepAskPassword'
import {
  useGetUserSecurityQuestionsQuery,
  useLoginMutation,
  useUserByCredentialsMutation
} from './AskPassword.codegen'
import { transitionAppStateToLoggedIn } from '../SessionHelpers'
import { notification } from '../../../components/common/notifications/Notifications'
import { t } from '@lingui/macro'
import { observer } from 'mobx-react'
import { FillEmptyAdditionalFieldsStep } from '../additional-info/FillEmptyAdditionalFieldsStep'
import {
  LoginContextType,
  LoginContextUserType,
  useLoginContext
} from '../login/LoginContext'
import { LoginEmailStep } from '../email/LoginEmailStep'
import { SetSecurityQuestionsAndLoginStep } from '../security-questions/SetSecurityQuestionsAndLoginStep'
import { useOrganisation } from 'front-end/src/public-api/hooks/useOrganisation'

const useLogin = () => {
  const ctx = useLoginContext()
  const [fetch] = useLoginMutation()
  const { data: orgData } = useOrganisation()

  return async () => {
    const { data } = await fetch({
      variables: {
        accountNameOrEmail: ctx.userIdentity,
        password: ctx.password
      }
    })
    if (data?.login) {
      const { user } = data?.login

      if (user.id) {
        transitionAppStateToLoggedIn(data?.login, orgData?.organisation)

        return true
      }
    }
    notification.error(t`Login failed. Please try again.`)

    return false
  }
}

const useVerifyUserByCredentials = () => {
  const [fetch] = useUserByCredentialsMutation()
  const ctx = useLoginContext()
  return async () => {
    const { data } = await fetch({
      variables: {
        accountNameOrEmail: ctx.userIdentity,
        password: ctx.password
      }
    })

    if (data?.userByCredentials) {
      ctx.setIsExistingUser(true)
      ctx.setUserData(data.userByCredentials as Partial<LoginContextUserType>)
    } else {
      ctx.setIsExistingUser(false)
    }
  }
}

const useFetchUserSecurityQuestions = () => {
  const ctx = useLoginContext()

  const { refetch: fetch } = useGetUserSecurityQuestionsQuery({
    skip: true
  })

  return async () => {
    if (ctx.userHasEmail || !ctx.isAccountNameLoginOn) {
      return []
    }

    if (ctx.usedSecurityQuestions) {
      return ctx.usedSecurityQuestions
    }

    const { data } = await fetch({
      accountName: ctx.userIdentity
    })
    ctx.setUsedSecurityQuestions(data.getUserSecurityQuestions ?? [])
    return data.getUserSecurityQuestions ?? []
  }
}

const userHasUnfilledAdditionalFields = (ctx: LoginContextType): boolean => {
  const requiredAdditionalFields = ctx.additionalFieldsList
  const userData: LoginContextUserType = ctx.userData
  return (
    !userData ||
    ctx.userNeedToAcceptPrivacyPolicy ||
    requiredAdditionalFields.some((field) => !userData[field])
  )
}

export const AskPasswordStep: React.FC = observer(({}) => {
  const ctx = useLoginContext()

  const login = useLogin()
  const verifyUserByCredentials = useVerifyUserByCredentials()
  const fetchUserSecurityQuestions = useFetchUserSecurityQuestions()

  const submit = async (): Promise<boolean> => {
    // check, if provided by user creds are valid and fetch user info
    await verifyUserByCredentials()

    if (ctx.isAccountNameLoginOn) {
      await fetchUserSecurityQuestions()
    }

    if (userHasUnfilledAdditionalFields(ctx)) {
      ctx.setStepView(FillEmptyAdditionalFieldsStep)
      return false
    }

    if (ctx.shouldAskSecurityQuestions()) {
      ctx.setStepView(SetSecurityQuestionsAndLoginStep)
      return false
    }

    return login()
  }

  const backToEmailStep = () => ctx.setStepView(LoginEmailStep)

  return (
    <LoginStepAskPassword submit={submit} backToEmailStep={backToEmailStep} />
  )
})
AskPasswordStep.displayName = 'AskPasswordStep'
